import { useRef, useState, type FormEvent } from "react";
import { Plus, X } from "lucide-react";
import type {
  FuenteReferencia,
  MotivoRechazo,
  PlataformaReferencia,
  SugerenciaDisponible,
  TipoReferencia,
} from "../datos/canalesReferencia";
import { MENSAJE_RECHAZO, sugerenciasDisponibles } from "../datos/canalesReferencia";
import { useCanalesReferencia } from "../datos/useCanalesReferencia";
import { useMaterialesConocimiento } from "../datos/useMaterialesConocimiento";
import { MarcoLogo, NOMBRE_MARCA, type MarcaLogo } from "./LogosMarcas";
import { MaterialesCargados } from "./MaterialesCargados";
import { ModalAnadirFuente, type AvisoAnadido } from "./ModalAnadirFuente";
import { CLASE_FOCO, CLASE_GRILLA, CLASE_TARJETA } from "./tarjetasFuente";

/**
 * "Canales de referencia": el primer bloque de la Base de conocimiento.
 *
 * Sirve para alimentar a Atlas con canales o videos de referentes que saben de
 * marketing, para que piense como ellos. Vienen cargadas por defecto las
 * fuentes oficiales de Meta; encima se pueden sumar referentes sugeridos de un
 * toque o pegar la URL de cualquier canal o video de YouTube, Instagram o
 * TikTok.
 *
 * HONESTIDAD SOBRE QUÉ PASA CON ESTO. Es la pantalla; el procesamiento se hace
 * después. Lo agregado se guarda en el NAVEGADOR (por marca), no en la cuenta,
 * y Atlas todavía no lo lee — y el cartel de arriba lo dice con todas las
 * letras, en la misma línea que el resto de la sección usa para lo que todavía
 * no funciona. Aceptar una fuente y tragársela en silencio, como si hubiera
 * quedado guardada en la cuenta, es exactamente lo que este bloque no hace.
 * El cartel tiene TRES versiones, porque no todo "todavía no se guardó" termina
 * igual: si el navegador no deja guardar (modo privado, storage lleno) lo
 * cargado se pierde al recargar y hay que decirlo; si lo único que falta es que
 * termine de cargar la marca, lo cargado se guarda solo apenas resuelve —
 * fusionado con lo que esa marca ya tenía— y avisar de una pérdida que no va a
 * ocurrir asusta sin motivo (ADR-2026-08-17-estudio-marketing-fixes-revision).
 *
 * UNA SOLA TARJETA PARA LOS DOS ESTADOS. Lo cargado y lo sugerido se dibujan
 * con la misma pieza —misma grilla de tres columnas que las conexiones, mismo
 * ancho, la mitad del alto— y se diferencian por el estado y la acción, no por
 * la forma: antes las sugerencias eran píldoras y parecían otra categoría de
 * cosa (ADR-2026-08-17-estudio-marketing-fuentes-tarjetas-media-altura).
 *
 * Ver ADR-2026-08-17-estudio-marketing-datos-canales-referencia.
 */

/** Las tres plataformas de las que se puede traer, con el logo que las
 *  identifica. `PlataformaReferencia` y `MarcaLogo` comparten los nombres, pero
 *  el mapa explícito evita que un rename silencioso de un lado rompa el otro. */
const LOGO_POR_PLATAFORMA: Record<PlataformaReferencia, MarcaLogo> = {
  youtube: "youtube",
  instagram: "instagram",
  tiktok: "tiktok",
};

/* Las clases de la tarjeta, la grilla, el foco y los rótulos viven en
   `tarjetasFuente.ts`: las comparten esta lista, las sugerencias y el material
   cargado desde el modal, y la única forma de que las tres grillas se sigan
   viendo como una sola es que salgan del mismo string. `MENSAJE_RECHAZO` se
   mudó a `datos/canalesReferencia.ts`, pegado al union que lo indexa, porque
   ahora también lo usa el modal (que agrega por el mismo camino y recibe los
   mismos rechazos). */

/** Las dos frases que separan los grupos, centradas. Reemplazan a los rótulos
 *  en versalita que Matías pidió sacar: el `mt-12` es el aire que separa un
 *  grupo del anterior. */
const CLASE_FRASE = "mt-12 mb-6 text-center text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.55)]";

/** La URL sin el ruido del protocolo: lo que importa es el canal, no el `https`. */
const urlCorta = (url: string) => url.replace(/^https?:\/\/(?:www\.)?/i, "");

/**
 * "Canal de YouTube", "Video de TikTok".
 *
 * La plataforma va en TEXTO y no sólo en el logo: `MarcoLogo` es
 * `aria-hidden` (es decorativo, no tiene rótulo), así que sin esta línea un
 * lector de pantalla escucha cinco veces "Gary Vaynerchuk" sin ninguna forma de
 * saber cuál es el de YouTube y cuál el de TikTok. Es, además, el dato que el
 * pedido exige que la tarjeta siga mostrando.
 */
const describirFuente = (tipo: TipoReferencia, plataforma: PlataformaReferencia) =>
  `${tipo === "canal" ? "Canal" : "Video"} de ${NOMBRE_MARCA[LOGO_POR_PLATAFORMA[plataforma]]}`;

/**
 * Lo que va adentro de CUALQUIERA de las dos tarjetas: logo, nombre y una línea
 * de detalle. Es un solo componente a propósito — una sugerencia y una fuente
 * cargada tienen que verse iguales, y la única forma de que sigan iguales
 * dentro de seis meses es que sean el mismo JSX. Lo que cambia es el marco (de
 * trazo lleno o punteado) y la acción de la derecha.
 *
 * La tipografía es la de las tarjetas de conexión (14px semibold + 12px al
 * 50%): misma familia, mitad de alto.
 */
const CuerpoTarjeta = ({
  marca,
  nombre,
  detalle,
}: {
  marca: MarcaLogo;
  nombre: string;
  detalle: string;
}) => (
  <>
    <MarcoLogo marca={marca} />
    {/* `<span>` y no `<div>`/`<p>`: este cuerpo también va DENTRO del `<button>`
        de `TarjetaSugerencia`, y el contenido de un `<button>` es phrasing
        content — un `<div>` o un `<p>` adentro es marcado inválido. Los `block`
        son los que mantienen las dos líneas (y el `truncate`) igual que antes;
        el contenedor no lo necesita porque los ítems de un flex se blockifican
        solos. Mismo criterio que el `Atajo` del modal. */}
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[14px] font-semibold text-brand-black">{nombre}</span>
      <span className="block truncate text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">{detalle}</span>
    </span>
  </>
);

/**
 * Una fuente YA cargada: marco de trazo LLENO sobre blanco y, a la derecha, la
 * acción de QUITAR (o el sello "Oficial de Meta" en las que no se pueden
 * quitar). Es la tarjeta "llena".
 */
const TarjetaFuente = ({ fuente, onQuitar }: { fuente: FuenteReferencia; onQuitar: () => void }) => (
  <li className="flex">
    {/* `title` con la URL entera: la línea de detalle la trunca en una columna
        de un tercio, y es el único lugar donde el enlace exacto se puede
        verificar sin abrirlo. */}
    <div
      className={`${CLASE_TARJETA} border border-[rgb(var(--brand-black-rgb)_/_0.08)] bg-white`}
      title={fuente.url}
    >
      <CuerpoTarjeta
        marca={LOGO_POR_PLATAFORMA[fuente.plataforma]}
        nombre={fuente.nombre}
        detalle={`${describirFuente(fuente.tipo, fuente.plataforma)} · ${urlCorta(fuente.url)}`}
      />
      {fuente.oficial ? (
        <span className="shrink-0 rounded-full bg-[#F0F2F7] py-1 px-2.5 text-[11px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.5)]">
          Oficial de Meta
        </span>
      ) : (
        <button
          type="button"
          onClick={onQuitar}
          aria-label={`Quitar ${fuente.nombre}`}
          className={`shrink-0 rounded-full p-1.5 text-[rgb(var(--brand-black-rgb)_/_0.4)] transition-colors hover:bg-[#F0F2F7] hover:text-[#C33B36] ${CLASE_FOCO}`}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  </li>
);

/**
 * Una sugerencia: la MISMA tarjeta (mismo ancho, mismo alto, misma grilla,
 * mismo cuerpo) en estado VACÍO.
 *
 * Se distingue de una cargada por tres cosas que NO son el color, porque el
 * color solo no alcanza:
 *  1. el marco es PUNTEADO y el fondo transparente — un hueco por llenar contra
 *     una tarjeta llena;
 *  2. la acción de la derecha es "Agregar" con un `+`, contra la `✕` de quitar;
 *  3. la línea de detalle arranca con la palabra "Sugerido".
 *
 * La tarjeta ENTERA es el botón (no un botón chiquito adentro): el gesto es
 * "agarro esta y la sumo", y así el blanco de 380×108 también es clickeable.
 * El `aria-label` le gana al texto de adentro, así que un lector de pantalla
 * anuncia la acción y no sólo el nombre.
 */
const TarjetaSugerencia = ({
  sugerencia,
  onAgregar,
}: {
  sugerencia: SugerenciaDisponible;
  onAgregar: () => void;
}) => {
  const detalle = describirFuente(sugerencia.tipo, sugerencia.plataforma);
  return (
    <li className="flex">
      <button
        type="button"
        onClick={onAgregar}
        title={sugerencia.url}
        aria-label={`Agregar ${sugerencia.nombre} — ${detalle}`}
        className={`group ${CLASE_TARJETA} border border-dashed border-[rgb(var(--brand-black-rgb)_/_0.22)] transition-colors hover:border-brand-primary hover:bg-white ${CLASE_FOCO}`}
      >
        <CuerpoTarjeta
          marca={LOGO_POR_PLATAFORMA[sugerencia.plataforma]}
          nombre={sugerencia.nombre}
          detalle={`Sugerido · ${detalle}`}
        />
        {/* `<span>` y no `<button>`: el botón es la tarjeta. Anidar uno adentro
            de otro es HTML inválido y deja dos paradas de tabulación para la
            misma acción. */}
        <span
          aria-hidden="true"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-brand-primary py-2 px-4 text-[12.5px] font-medium text-brand-primary transition-colors group-hover:bg-brand-primary group-hover:text-white"
        >
          <Plus className="h-3.5 w-3.5" />
          Agregar
        </span>
      </button>
    </li>
  );
};

export const CanalesReferencia = ({
  brandId,
  titulo,
}: {
  brandId: string | null;
  /**
   * El encabezado de la sección lo dibuja ESTE componente, no la pantalla, por
   * un motivo de layout: Matías pidió el botón "Añadir fuente" a la misma
   * altura que el título, y para eso los dos tienen que ser hermanos en la
   * misma fila flex. Con el h2 en la pantalla y el botón acá adentro quedaban
   * en dos bloques distintos y no había forma de alinearlos sin inventar
   * márgenes negativos.
   */
  titulo: string;
}) => {
  const { fuentes, agregadas, agregar, quitar, persiste, motivoSinPersistir } =
    useCanalesReferencia(brandId);
  const materiales = useMaterialesConocimiento(brandId);
  const [texto, setTexto] = useState("");
  const [rechazo, setRechazo] = useState<MotivoRechazo | null>(null);
  /** Lo último que entró, para poder decirlo. Un solo estado para las dos
   *  listas: los dos avisos ocupan el mismo renglón y mostrar dos a la vez
   *  sería contar dos veces la misma acción. */
  const [aviso, setAviso] = useState<AvisoAnadido | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  /** El botón que abre el modal: ahí vuelve el foco al cerrarlo. */
  const botonAnadirRef = useRef<HTMLButtonElement>(null);

  const sumar = (url: string, nombre?: string) => {
    const resultado = agregar(url, nombre);
    if (!resultado.ok) {
      setRechazo(resultado.motivo);
      setAviso(null);
      return false;
    }
    setRechazo(null);
    setAviso({ clase: "canal", nombre: resultado.fuente.nombre });
    return true;
  };

  const alEnviar = (evento: FormEvent) => {
    evento.preventDefault();
    if (sumar(texto)) setTexto("");
  };

  const sugerencias = sugerenciasDisponibles(agregadas);

  /** Cómo termina el "Agregamos X a la lista". Va como variable y no inline en
   *  el JSX para que el punto final no quede separado por el espacio que mete
   *  JSX cuando la expresión arranca en otra línea ("a la lista ."). */
  const colaAgregada = persiste
    ? " y quedó guardada en este navegador."
    : motivoSinPersistir === "sin-marca"
      ? " y se va a guardar apenas termine de cargar tu marca."
      : ".";

  /**
   * La cola del aviso del material. Es OTRA que la de los canales porque el
   * cuerpo es otro ("Anotamos “x.pdf”. El archivo NO se subió…", que ya trae un
   * punto adentro) y porque el material tiene su propio estado de persistencia:
   * un texto largo puede pasarse de la cuota del navegador aunque los canales
   * estén entrando bien, y ahí hay que decir que eso NO sobrevive al reload.
   */
  const colaMaterial = materiales.persiste
    ? ", en este navegador."
    : materiales.motivoSinPersistir === "sin-marca"
      ? ". Se termina de guardar apenas cargue tu marca."
      : ". Pero este navegador no nos deja guardarlo: vale mientras no cierres la pestaña.";

  /**
   * EL CARTEL HABLA DE TODO EL BLOQUE, así que mira las DOS listas.
   *
   * Son dos hooks de persistencia independientes (canales y material) y el
   * cartel se dibujaba sólo con el de los CANALES. Como `storageOk` arranca en
   * `true` y sólo se reescribe al escribir, un usuario que nunca agregó un
   * canal y pegó un texto que no entró en la cuota veía la primera variante
   * —"queda guardado en este navegador"— sobre algo que no quedó guardado en
   * ningún lado: la misma mentira que esta pantalla existe para evitar, un
   * escalón más abajo. Y valía al revés: un canal que no se pudo guardar hacía
   * decir "este navegador no nos deja guardar nada" con el material entrando
   * bien.
   *
   * `storage` GANA sobre `sin-marca`: es el único de los dos que termina en
   * pérdida, y callarlo porque la otra lista está esperando la marca sería
   * elegir la mitad tranquilizadora de la verdad.
   */
  /* `persisteTodo` y `motivoGeneral` alimentaban el cartel ámbar, que Matías
     pidió sacar. Se van con él: eran su única lectura. Si el aviso vuelve,
     vuelven estas dos líneas y el tipo `MotivoSinPersistir` que las tipa. */

  return (
    <div>
      {/* El encabezado de la sección y el botón, EN LA MISMA FILA. El h3 que
          decía "Canales de referencia" lo sacó Matías; el que se dibuja acá es
          el de la sección entera ("Fuentes de datos"), que antes vivía en la
          pantalla. NO devolver el h3 ni subir el h2 de vuelta: quedarían en dos
          bloques y el botón dejaría de estar a la altura del título. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[16px] font-semibold text-brand-black">{titulo}</h2>
        <button
          type="button"
          ref={botonAnadirRef}
          onClick={() => setModalAbierto(true)}
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-brand-primary py-2.5 px-5 text-[13px] font-medium text-white transition-opacity hover:opacity-90 ${CLASE_FOCO}`}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Añadir fuente
        </button>
      </div>
      {/* Sin bajada: Matías pidió sacar los subtítulos de esta pantalla. La que
          había acá era la que explicaba PARA QUÉ sirve el bloque —canales de
          referentes de marketing, para que Atlas piense como ellos—, así que es
          la primera candidata a volver si la pantalla queda muda para alguien
          que la ve por primera vez. Si vuelve, va en futuro: el cartel de abajo
          dice que Atlas todavía no lo lee, y una bajada en presente lo
          desmentiría cuatro líneas antes. */}

      {/* El cartel ámbar que estaba acá lo sacó Matías por pedido explícito.
          NO REVERTIR. Decía que lo cargado queda en este navegador, no en la
          cuenta, y que Atlas todavía no lo lee. Esa información NO se perdió
          del todo: el modal de "Añadir fuente" la repite en cada camino, y las
          tarjetas de archivo llevan el sello "Sin subir". Si algún día el
          bloque vuelve a necesitar el aviso general, esto es lo que decía. */}

      {/* ── La lista cargada ──
          Sin rótulo: Matías pidió sacar los tres ("Fuentes cargadas", "Sumar un
          referente", "Agregar la tuya"). Lo que separa cada grupo ahora es el
          aire y las dos frases centradas. NO devolverlos. */}
      <ul className={CLASE_GRILLA}>
        {fuentes.map((fuente) => (
          <TarjetaFuente
            key={fuente.id}
            fuente={fuente}
            onQuitar={() => {
              quitar(fuente.id);
              setAviso(null);
              setRechazo(null);
            }}
          />
        ))}
      </ul>

      {/* ── El material que no es un canal ──
          Archivos anotados, páginas y textos que entraron por "Añadir fuente".
          Va acá, pegado a las fuentes cargadas y con la MISMA tarjeta, porque
          es material de la misma base de conocimiento: el botón que lo carga
          está en este bloque y lo cargado tiene que aparecer donde el usuario
          lo está mirando. */}
      <MaterialesCargados
        materiales={materiales.materiales}
        onQuitar={(id) => {
          materiales.quitar(id);
          setAviso(null);
          setRechazo(null);
        }}
      />

      {/* ── Sugerencias de referentes ──
          Misma grilla y misma tarjeta que la lista de arriba: lo que separa una
          sugerencia de una fuente cargada es su ESTADO y su ACCIÓN (marco
          punteado + "Agregar", contra marco lleno + "quitar"), no una forma
          distinta. Antes eran píldoras y parecían otra cosa. */}
      {sugerencias.length > 0 && (
        <>
          {/* `mt-12` y no `mt-6`: Matías pidió más aire entre lo cargado y las
              sugerencias, que son dos cosas distintas (lo que ya tenés contra lo
              que te ofrecemos) y con seis unidades se leían como una sola lista
              larga. */}
          <p className={CLASE_FRASE}>Te sugerimos las siguientes fuentes</p>
          <ul className={CLASE_GRILLA}>
            {sugerencias.map((sugerencia) => (
              <TarjetaSugerencia
                key={sugerencia.url}
                sugerencia={sugerencia}
                onAgregar={() => sumar(sugerencia.url, sugerencia.nombre)}
              />
            ))}
          </ul>
        </>
      )}

      {/* ── Agregar una propia ── */}
      <p className={CLASE_FRASE}>o agregá la tuya</p>
      <form onSubmit={alEnviar} className="mt-4">
        {/* El `<label>` sigue existiendo para quien usa lector de pantalla, pero
            va oculto: la frase centrada de arriba hace de rótulo visual y no
            puede serlo también del campo sin quedar pegada y desalineada. */}
        <label htmlFor="canal-referencia-url" className="sr-only">
          Agregá la URL de un canal o de un video
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id="canal-referencia-url"
            type="text"
            value={texto}
            onChange={(evento) => {
              setTexto(evento.target.value);
              setRechazo(null);
              setAviso(null);
            }}
            placeholder="Pegá la URL de un canal o de un video"
            // `inputMode`/`autoComplete` en off: es una URL pegada, no un campo
            // que el navegador pueda completar con algo razonable.
            autoComplete="off"
            spellCheck={false}
            aria-invalid={rechazo !== null}
            aria-describedby={rechazo ? "canal-referencia-error" : undefined}
            className={`min-w-0 flex-1 rounded-xl border bg-white py-2.5 px-4 text-[13px] text-brand-black outline-none transition-colors placeholder:text-[rgb(var(--brand-black-rgb)_/_0.35)] focus:border-brand-primary ${
              rechazo ? "border-[#C33B36]" : "border-[rgb(var(--brand-black-rgb)_/_0.08)]"
            }`}
          />
          <button
            type="submit"
            className="shrink-0 rounded-xl bg-brand-primary py-2.5 px-5 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
          >
            Agregar
          </button>
        </div>

        {rechazo && (
          <p id="canal-referencia-error" role="alert" className="mt-2 text-[12.5px] text-[#C33B36]">
            {MENSAJE_RECHAZO[rechazo]}
          </p>
        )}
        {/* Un solo renglón para los dos caminos (el formulario de acá y el
            modal), con el texto que corresponda: el canal dice "a la lista" y
            el material dice qué se guardó y qué NO se subió. */}
        {aviso && !rechazo && (
          <p role="status" className="mt-2 text-[12.5px] leading-relaxed text-[#1D7A57]">
            {aviso.clase === "canal" ? (
              <>
                Agregamos “{aviso.nombre}” a la lista{colaAgregada}
              </>
            ) : (
              <>
                {aviso.texto}
                {colaMaterial}
              </>
            )}
          </p>
        )}
      </form>

      {modalAbierto && (
        <ModalAnadirFuente
          onCerrar={() => setModalAbierto(false)}
          volverElFocoA={botonAnadirRef}
          // Agrega por el MISMO camino que el formulario de arriba: mismo
          // dedup, mismos rechazos, misma persistencia. El modal no toca el
          // storage ni conoce la lista.
          agregarCanal={agregar}
          agregarMateriales={materiales.agregar}
          onGuardado={(nuevo) => {
            setRechazo(null);
            setAviso(nuevo);
          }}
        />
      )}
    </div>
  );
};

export default CanalesReferencia;
